import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Clock, Flame, Store, UtensilsCrossed, Lightbulb, CornerDownLeft, Loader2, Sparkles } from 'lucide-react';
import { host } from './script/variables';
import './style/FoodSearch.css';

/* ================================================================
   FoodSearch — the one search box for dishes, restaurant names,
   cuisines, vibes and occasions. The server interprets free text
   (POST /outlet/search); this box shows live, grouped suggestions
   from what actually exists nearby (GET /outlet/suggest), plus
   recent + trending searches when empty. Full keyboard support.
================================================================ */

const EMPTY = { restaurants: [], dishes: [], tags: [], ideas: [] };
const RECENT_KEY = 'rp_recent_searches';
const MAX_RECENT = 8;
const GROUP_LABEL = { cuisine: 'Cuisine', ambience: 'Ambience', vibe: 'Vibe', occasion: 'Good for', veg: 'Diet', open: 'Timing' };

const readRecent = () => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  } catch { return []; }
};
const writeRecent = (list) => { try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* private mode */ } };

const ICON = { restaurant: Store, dish: UtensilsCrossed, idea: Lightbulb, search: Search };

const FoodSearch = ({ value, onSubmit, coords, onOpenRestaurant }) => {
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sug, setSug] = useState(EMPTY);
  const [home, setHome] = useState(null);
  const [recent, setRecent] = useState(readRecent);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const ctrlRef = useRef(null);

  useEffect(() => { setText(value || ''); }, [value]);

  // Close when clicking outside
  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Trending + quick picks for the empty box (once)
  useEffect(() => {
    if (!open || home) return;
    fetch(`${host}/outlet/suggest?q=`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setHome({ trending: d.trending || [], quick: d.quick || [] }))
      .catch(() => setHome({ trending: [], quick: [] }));
  }, [open, home]);

  // Live suggestions — debounced, stale requests aborted
  useEffect(() => {
    const t = text.trim();
    if (!t) { setSug(EMPTY); setBusy(false); return undefined; }
    setBusy(true);
    const timer = setTimeout(async () => {
      ctrlRef.current?.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;
      try {
        const params = new URLSearchParams({ q: t });
        if (coords?.lat != null && coords?.lng != null) { params.set('lat', coords.lat); params.set('lng', coords.lng); }
        const r = await fetch(`${host}/outlet/suggest?${params}`, { signal: ctrl.signal });
        const d = r.ok ? await r.json() : EMPTY;
        setSug({ ...EMPTY, ...d });
        setActive(-1);
      } catch (e) {
        if (e.name !== 'AbortError') setSug(EMPTY);
      } finally {
        if (ctrlRef.current === ctrl) setBusy(false);
      }
    }, 160);
    return () => clearTimeout(timer);
  }, [text, coords?.lat, coords?.lng]);

  const t = text.trim();

  // Flat list of everything selectable (drives keyboard navigation)
  const items = useMemo(() => {
    if (!t) {
      return [
        ...recent.map((r) => ({ kind: 'recent', label: r, q: r })),
        ...(home?.trending || []).map((x) => ({ kind: 'trending', label: x, q: x })),
        ...(home?.quick || []).map((x) => ({ kind: 'quick', label: x.label, emoji: x.emoji, q: x.value })),
      ];
    }
    return [
      { kind: 'search', label: `Search “${t}”`, sub: 'Restaurants, dishes, cuisines and vibes', q: t },
      ...sug.restaurants.map((r) => ({ kind: 'restaurant', label: r.name, sub: `${r.distanceKm} km away`, id: r.id })),
      ...sug.dishes.map((d) => ({ kind: 'dish', label: d.name, sub: `${d.places} place${d.places === 1 ? '' : 's'} · nearest ${d.nearestKm} km`, q: d.name })),
      ...sug.tags.map((g) => ({ kind: 'tag', label: g.label, emoji: g.emoji, sub: GROUP_LABEL[g.group], q: g.label })),
      ...sug.ideas.map((i) => ({ kind: 'idea', label: i.label, sub: i.hint, q: i.value })),
    ];
  }, [t, recent, home, sug]);

  const remember = (q) => {
    const v = String(q || '').trim().slice(0, 60);
    if (!v) return;
    const list = [v, ...readRecent().filter((x) => x.toLowerCase() !== v.toLowerCase())].slice(0, MAX_RECENT);
    writeRecent(list);
    setRecent(list);
  };

  const submit = (q) => {
    const v = String(q ?? text).trim();
    setOpen(false);
    setActive(-1);
    setText(v);
    inputRef.current?.blur();
    remember(v);
    onSubmit(v);
  };

  const pick = (it) => {
    if (it.kind === 'restaurant') {
      setOpen(false);
      remember(it.label);
      onOpenRestaurant(it.id);
    } else {
      submit(it.q);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(-1, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && active >= 0 && items[active]) pick(items[active]); else submit(); }
    else if (e.key === 'Escape') { setOpen(false); setActive(-1); }
  };

  const clear = () => {
    setText('');
    setSug(EMPTY);
    if (value) onSubmit('');
    inputRef.current?.focus();
  };

  const idx = (it) => items.indexOf(it);
  const optionProps = (it) => ({
    id: `fsr-opt-${idx(it)}`,
    role: 'option',
    'aria-selected': active === idx(it),
    onMouseEnter: () => setActive(idx(it)),
    onMouseDown: (e) => e.preventDefault(),   // keep focus in the input
    onClick: () => pick(it),
  });

  const chips = (kind, icon) => items.filter((x) => x.kind === kind).map((it) => (
    <button key={`${kind}-${it.label}`} type="button" className={`fsr-chip ${active === idx(it) ? 'fsr-chip--active' : ''}`} {...optionProps(it)}>
      {icon}{it.emoji && <span aria-hidden="true">{it.emoji}</span>}{it.label}
    </button>
  ));

  const rows = (kind, title) => {
    const list = items.filter((x) => x.kind === kind);
    if (!list.length) return null;
    return (
      <div className="fsr-group" role="group" aria-label={title || kind}>
        {title && <div className="fsr-group-title">{title}</div>}
        {list.map((it) => {
          const Icon = ICON[kind];
          return (
            <div key={`${kind}-${it.id || it.label}`} className={`fsr-row ${active === idx(it) ? 'fsr-row--active' : ''}`} {...optionProps(it)}>
              <span className="fsr-row-icon" aria-hidden="true">{it.emoji || (Icon ? <Icon size={15} /> : <Sparkles size={15} />)}</span>
              <span className="fsr-row-text">
                <span className="fsr-row-title">{it.label}</span>
                {it.sub && <span className="fsr-row-sub">{it.sub}</span>}
              </span>
              {kind === 'search' && <CornerDownLeft size={14} className="fsr-row-hint" />}
            </div>
          );
        })}
      </div>
    );
  };

  const nothing = t && !sug.restaurants.length && !sug.dishes.length && !sug.tags.length && !sug.ideas.length;

  return (
    <div className="fsr-wrap" ref={wrapRef}>
      <div className={`fsr-bar ${open ? 'fsr-bar--open' : ''}`}>
        {busy ? <Loader2 size={18} className="fsr-icon fsr-spin" /> : <Search size={18} className="fsr-icon" />}
        <input
          ref={inputRef}
          className="fsr-input"
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search a dish, restaurant, cuisine or vibe…"
          role="combobox"
          aria-expanded={open}
          aria-controls="fsr-listbox"
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `fsr-opt-${active}` : undefined}
          aria-label="Search restaurants"
          autoComplete="off"
          spellCheck={false}
        />
        {text && (
          <button type="button" className="fsr-clear" onClick={clear} aria-label="Clear search"><X size={16} /></button>
        )}
        <button type="button" className="fsr-go" onClick={() => submit()}>Search</button>
      </div>

      {open && (
        <div className="fsr-panel" id="fsr-listbox" role="listbox" aria-label="Search suggestions">
          {!t ? (
            <>
              {recent.length > 0 && (
                <div className="fsr-group">
                  <div className="fsr-group-title">
                    Recent
                    <button type="button" className="fsr-link" onMouseDown={(e) => e.preventDefault()} onClick={() => { writeRecent([]); setRecent([]); }}>Clear</button>
                  </div>
                  <div className="fsr-chips">{chips('recent', <Clock size={13} />)}</div>
                </div>
              )}
              {!!home?.trending?.length && (
                <div className="fsr-group">
                  <div className="fsr-group-title"><Flame size={13} /> Trending</div>
                  <div className="fsr-chips">{chips('trending')}</div>
                </div>
              )}
              <div className="fsr-group">
                <div className="fsr-group-title">Try</div>
                {home ? <div className="fsr-chips">{chips('quick')}</div> : <div className="fsr-muted">Loading…</div>}
              </div>
            </>
          ) : (
            <>
              {rows('search')}
              {rows('restaurant', 'Restaurants')}
              {rows('dish', 'Dishes nearby')}
              {rows('tag', 'Cuisines & vibes')}
              {rows('idea', 'Ideas')}
              {!busy && nothing && <div className="fsr-muted">No direct matches yet — search anyway and we’ll show the closest places.</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FoodSearch;
